import { html, LitElement } from "lit";
import { customElement, property, queryAssignedElements } from 'lit/decorators.js';
import style from './usos-link.scss';
import { when } from "lit/directives/when.js";
import { MaterialIconType } from "../icon/usos-icon";

/**
 * Preconfigured button-like link for "go to" navigation.
 *
 * @slot - link contents
 */
@customElement('usos-link')
export class UsosLink extends LitElement {
    static override shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };
    static override styles = style;

    /** Icon location. Can be ommited not to show icon. */
    @property({ attribute: 'icon-location', reflect: true }) iconLocation: 'right' | 'left' | null = null;

    /** Icon name, {@see UsosLink.iconName} */
    @property({ attribute: 'icon-name', reflect: true }) iconName: Optional<string> = null;

    /** Should link have button-like border? */
    @property({ attribute: 'bordered', reflect: true, type: Boolean }) bordered = false;

    /**
     * Should link be inline?
     * If link is not inline, it has predefined minimum height (to meet accessibility standards). However, links inside longer
     * text can have smaller height, if this is desired, this attribute can be set.
     */
    @property({ attribute: 'inline', reflect: true, type: Boolean }) inline = false;

    @queryAssignedElements({selector: 'a'})
    assignedLinks!: HTMLAnchorElement[];

    override render() {
        const iconLocation = this.iconLocation ?? (this.iconName ? 'left' : null);
        return html`
            ${when(iconLocation == 'left', () => html`<usos-icon id="icon-left" .iconName=${this._getIconName()} .iconType=${this._getIconType()}></usos-icon>`)}
            <slot ?data-icon-left=${iconLocation == 'left'} ?data-icon-right=${iconLocation == 'right'}></slot>
            ${when(iconLocation == 'right', () => html`<usos-icon id="icon-right" .iconName=${this._getIconName()} .iconType=${this._getIconType()}></usos-icon>`)}
        `;
    }

    private _getIconName(): Optional<string> {
        if (!this.iconName && this.iconLocation != null) {
            // default arrow icons
            return this.iconLocation == 'left' ? 'west' : 'east';
        } else
            return this.iconName?.slice(this.iconName.indexOf('.') + 1) ?? null;
    }

    private _getIconType(): Optional<MaterialIconType> {
        if (this.iconName?.includes('.')) {
            return this.iconName.slice(0, this.iconName.indexOf('.')) as MaterialIconType;
        } else if (this.iconName) {
            return 'filled';
        } else {
            return null;
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'usos-link': UsosLink,
    }
}
